import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { vehicleAPI, documentAPI, reminderAPI, expenseAPI, maintenanceAPI } from '../api/client';
import { Car, ArrowLeft, Edit, Archive, FileText, Bell, Wallet, Wrench, Upload, Download, Trash2, Plus, Calendar, Camera } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { VehiclePhoto } from '../components/VehiclePhoto';
import { toast } from 'sonner';
import { format, parseISO, differenceInDays } from 'date-fns';

const VehicleDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const [vehicle, setVehicle] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [maintenance, setMaintenance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoRefreshKey, setPhotoRefreshKey] = useState(0);
  const photoInputRef = useRef(null);

  useEffect(() => {
    fetchVehicleData();
  }, [id]);

  const fetchVehicleData = async () => {
    try {
      const [vehicleRes, docsRes, remindersRes, expensesRes, maintenanceRes] = await Promise.all([
        vehicleAPI.get(id),
        documentAPI.list(id),
        reminderAPI.list(id),
        expenseAPI.list({ vehicle_id: id }),
        maintenanceAPI.list(id)
      ]);
      setVehicle(vehicleRes.data);
      setDocuments(docsRes.data);
      setReminders(remindersRes.data);
      setExpenses(expensesRes.data);
      setMaintenance(maintenanceRes.data);
    } catch (error) {
      toast.error('Failed to load vehicle details');
      navigate('/app/vehicles');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    setUploading(true);
    const formData = new FormData(e.target);
    try {
      await documentAPI.upload(id, formData);
      toast.success('Document uploaded successfully');
      setUploadDialogOpen(false);
      fetchVehicleData();
    } catch (error) {
      toast.error('Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (docId, filename) => {
    try {
      const response = await documentAPI.download(docId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  const handleDelete = async (docId) => {
    if (!confirm('Are you sure you want to delete this document?')) return;
    try {
      await documentAPI.delete(docId);
      toast.success('Document deleted');
      fetchVehicleData();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const handleVehiclePhotoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Photo must be under 10MB');
      return;
    }
    setPhotoUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const resp = await fetch(`${backendUrl}/api/vehicles/${id}/photo`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      if (!resp.ok) throw new Error('Upload failed');
      toast.success('Photo updated');
      // Force VehiclePhoto to re-fetch the presigned URL
      setPhotoRefreshKey((k) => k + 1);
    } catch (err) {
      console.error(err);
      toast.error('Failed to upload photo');
    } finally {
      setPhotoUploading(false);
      if (photoInputRef.current) photoInputRef.current.value = '';
    }
  };

  const getStatusBadge = (date) => {
    if (!date) return null;
    const days = differenceInDays(parseISO(date), new Date());
    if (days < 0) return <Badge className="status-overdue">Overdue</Badge>;
    if (days <= 30) return <Badge className="status-due-soon">Due Soon</Badge>;
    return <Badge className="status-ok">OK</Badge>;
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const totalExpenses = expenses.reduce((sum, exp) => sum + exp.amount, 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Button variant="ghost" onClick={() => navigate('/app/vehicles')} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Vehicles
        </Button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-semibold tracking-tight">
              {vehicle.year} {vehicle.make} {vehicle.model}
            </h1>
            <p className="text-muted-foreground mt-1 font-mono text-lg">{vehicle.reg_plate}</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm">
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
            <Button variant="outline" size="sm">
              <Archive className="h-4 w-4 mr-2" />
              Archive
            </Button>
          </div>
        </div>
      </div>

      {/* Vehicle hero photo */}
      <Card className="overflow-hidden">
        <div className="relative">
          <VehiclePhoto
            key={photoRefreshKey}
            vehicleId={id}
            alt={`${vehicle.make} ${vehicle.model}`}
            className="h-56 sm:h-72 w-full overflow-hidden"
          />
          <input
            ref={photoInputRef}
            type="file"
            accept="image/*"
            onChange={handleVehiclePhotoUpload}
            className="hidden"
            data-testid="vehicle-detail-photo-input"
          />
          <Button
            variant="secondary"
            size="sm"
            onClick={() => photoInputRef.current?.click()}
            disabled={photoUploading}
            className="absolute top-3 right-3 shadow"
            data-testid="vehicle-detail-photo-upload"
          >
            <Camera className="h-4 w-4 mr-2" />
            {photoUploading ? 'Uploading…' : 'Change photo'}
          </Button>
        </div>
      </Card>

      {/* Quick Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">MOT Expiry</p>
                <p className="text-lg font-semibold">
                  {vehicle.mot_expiry ? format(parseISO(vehicle.mot_expiry), 'dd/MM/yyyy') : 'Not set'}
                </p>
              </div>
              {getStatusBadge(vehicle.mot_expiry)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Tax Expiry</p>
                <p className="text-lg font-semibold">
                  {vehicle.tax_expiry ? format(parseISO(vehicle.tax_expiry), 'dd/MM/yyyy') : 'Not set'}
                </p>
              </div>
              {getStatusBadge(vehicle.tax_expiry)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Insurance</p>
                <p className="text-lg font-semibold">
                  {vehicle.insurance_expiry ? format(parseISO(vehicle.insurance_expiry), 'dd/MM/yyyy') : 'Not set'}
                </p>
              </div>
              {getStatusBadge(vehicle.insurance_expiry)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div>
              <p className="text-sm text-muted-foreground">Total Expenses</p>
              <p className="text-lg font-semibold">£{totalExpenses.toFixed(2)}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="documents" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="documents" data-testid="documents-tab">
            <FileText className="h-4 w-4 mr-2" />
            Documents ({documents.length})
          </TabsTrigger>
          <TabsTrigger value="reminders">
            <Bell className="h-4 w-4 mr-2" />
            Reminders ({reminders.length})
          </TabsTrigger>
          <TabsTrigger value="expenses">
            <Wallet className="h-4 w-4 mr-2" />
            Expenses ({expenses.length})
          </TabsTrigger>
          <TabsTrigger value="maintenance">
            <Wrench className="h-4 w-4 mr-2" />
            Maintenance ({maintenance.length})
          </TabsTrigger>
        </TabsList>

        {/* Documents Tab */}
        <TabsContent value="documents" className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-muted-foreground">Store MOT, insurance, V5C, and service receipts</p>
            <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="upload-document-button">
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Document
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Upload Document</DialogTitle>
                  <DialogDescription>Upload a document for this vehicle (max 10MB)</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleUpload} className="space-y-4">
                  <div>
                    <Label htmlFor="file">File</Label>
                    <Input id="file" name="file" type="file" required accept=".pdf,.jpg,.jpeg,.png" />
                  </div>
                  <div>
                    <Label htmlFor="category">Category</Label>
                    <Select name="category" required>
                      <SelectTrigger>
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mot">MOT Certificate</SelectItem>
                        <SelectItem value="insurance">Insurance</SelectItem>
                        <SelectItem value="v5c">V5C (Logbook)</SelectItem>
                        <SelectItem value="receipt">Receipt</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="notes">Notes (optional)</Label>
                    <Input id="notes" name="notes" placeholder="Add notes about this document" />
                  </div>
                  <Button type="submit" disabled={uploading} className="w-full">
                    {uploading ? 'Uploading...' : 'Upload'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {documents.length > 0 ? (
            <div className="grid gap-4">
              {documents.map((doc) => (
                <Card key={doc.document_id}>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="bg-primary/10 p-3 rounded-lg">
                          <FileText className="h-6 w-6 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium">{doc.file_name}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline">{doc.category}</Badge>
                            <span className="text-sm text-muted-foreground">
                              {(doc.file_size / 1024).toFixed(0)} KB
                            </span>
                            <span className="text-sm text-muted-foreground">
                              {format(parseISO(doc.uploaded_at), 'dd/MM/yyyy')}
                            </span>
                          </div>
                          {doc.notes && <p className="text-sm text-muted-foreground mt-1">{doc.notes}</p>}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownload(doc.document_id, doc.file_name)}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(doc.document_id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground mb-4">No documents uploaded yet</p>
                <Button onClick={() => setUploadDialogOpen(true)}>Upload First Document</Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Reminders Tab */}
        <TabsContent value="reminders" className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-muted-foreground">Reminders for this vehicle</p>
            <Button asChild>
              <Link to="/app/reminders">
                <Plus className="h-4 w-4 mr-2" />
                Add Reminder
              </Link>
            </Button>
          </div>

          {reminders.length > 0 ? (
            <div className="grid gap-4">
              {reminders.map((reminder) => {
                const days = differenceInDays(parseISO(reminder.due_date), new Date());
                let status;
                if (days < 0) status = { label: 'Overdue', className: 'status-overdue' };
                else if (days <= 7) status = { label: 'Due Soon', className: 'status-due-soon' };
                else status = { label: 'OK', className: 'status-ok' };

                return (
                  <Card key={reminder.reminder_id}>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <Calendar className="h-6 w-6 text-muted-foreground" />
                          <div>
                            <p className="font-medium">{reminder.title}</p>
                            <p className="text-sm text-muted-foreground">
                              Due: {format(parseISO(reminder.due_date), 'dd MMMM yyyy')}
                            </p>
                          </div>
                        </div>
                        <Badge className={status.className}>{status.label}</Badge>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <Bell className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground mb-4">No reminders set for this vehicle</p>
                <Button asChild>
                  <Link to="/app/reminders">Create Reminder</Link>
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Expenses Tab */}
        <TabsContent value="expenses" className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-muted-foreground">Expense history for this vehicle</p>
            <Button asChild>
              <Link to="/app/expenses">
                <Plus className="h-4 w-4 mr-2" />
                Add Expense
              </Link>
            </Button>
          </div>

          {expenses.length > 0 ? (
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Total Expenses</CardTitle>
                  <CardDescription>All time spending on this vehicle</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-semibold">£{totalExpenses.toFixed(2)}</p>
                </CardContent>
              </Card>

              <div className="grid gap-4">
                {expenses.slice(0, 10).map((expense) => (
                  <Card key={expense.expense_id}>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium capitalize">{expense.category}</p>
                          <p className="text-sm text-muted-foreground">
                            {format(parseISO(expense.date), 'dd MMM yyyy')}
                            {expense.description && ` • ${expense.description}`}
                          </p>
                        </div>
                        <p className="text-lg font-semibold">£{expense.amount.toFixed(2)}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {expenses.length > 10 && (
                <Button variant="outline" className="w-full" asChild>
                  <Link to="/app/expenses">View All Expenses</Link>
                </Button>
              )}
            </div>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <Wallet className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground mb-4">No expenses recorded yet</p>
                <Button asChild>
                  <Link to="/app/expenses">Add First Expense</Link>
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Maintenance Tab */}
        <TabsContent value="maintenance" className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-muted-foreground">Service and repair history</p>
            <Button asChild>
              <Link to="/app/maintenance">
                <Plus className="h-4 w-4 mr-2" />
                Add Service Record
              </Link>
            </Button>
          </div>

          {maintenance.length > 0 ? (
            <div className="grid gap-4">
              {maintenance.map((record) => (
                <Card key={record.maintenance_id}>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-4">
                      <div className="bg-primary/10 p-3 rounded-lg">
                        <Wrench className="h-6 w-6 text-primary" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <p className="font-medium capitalize">{record.type}</p>
                          {record.cost && <p className="font-semibold">£{record.cost.toFixed(2)}</p>}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {format(parseISO(record.date), 'dd MMM yyyy')} • {record.mileage.toLocaleString()} miles
                        </p>
                        <p className="text-sm mt-2">{record.description}</p>
                        {record.mechanic_name && (
                          <p className="text-sm text-muted-foreground mt-1">By: {record.mechanic_name}</p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <Wrench className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground mb-4">No maintenance records yet</p>
                <Button asChild>
                  <Link to="/app/maintenance">Add First Service</Link>
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default VehicleDetail;
